import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { AbsencesService } from '@wilson/absences';
import {
  Absence,
  AbsenceWithDecisionAndHighlighted,
  GeoLocation,
  TimelineShift,
  TimelineUserDTO,
  UnassignedShift,
  UserTimelines,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import { isDebugEnabled } from '@wilson/non-domain-specific/logger/logger';
import {
  calculateUserAssignmentStack,
  determineAssignmentsOverlaps,
} from '@wilson/non-domain-specific/overlap-helpers';
import { ShiftsService } from '@wilson/shifts';
import { chunk } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, firstValueFrom, map, of } from 'rxjs';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

const SHIFT_RELATIONS = [
  'organizationalUnit',
  'user',
  'shiftCategory',
  'shiftSeries',
  'labels',
  'startLocation',
  'endLocation',
];

@Injectable({
  providedIn: 'root',
})
export class ShiftOptimizerDataHelperService {
  private readonly shiftsService = inject(ShiftsService);
  private readonly locationsStoreService = inject(LocationsStoreService);
  private readonly absencesService = inject(AbsencesService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);

  public generateUserTimelinesWithHomeLocation(
    user: TimelineUserDTO,
  ): Observable<UserTimelines> {
    const locationId = user.organizationalUnit?.locationId;
    if (locationId) {
      return this.locationsStoreService
        .getLocationFromCache(locationId)
        .pipe(map((location) => this.generateUserTimelines(user, location)));
    } else {
      return of(this.generateUserTimelines(user, null));
    }
  }

  private generateUserTimelines(
    user: TimelineUserDTO,
    location: GeoLocation | null | undefined,
  ): UserTimelines {
    return {
      ...user,
      organizationalUnit: {
        ...user.organizationalUnit,
        location: location || undefined,
      },
      userRoles: user.userRoles ?? [],
      determinedRowDataV2: [],
      determinedRowDataTimestamp: 0,
      absencesDictionary: {},
      shiftsWithoutActivitiesDictionary: {},
      staysDictionary: {},
      assignmentStacks: [],
    };
  }

  public async fetchShifts(shiftIds: string[]): Promise<TimelineShift[]> {
    if (shiftIds.length) {
      const shiftsRecord = await firstValueFrom(
        this.shiftsService.getShiftsByIds<TimelineShift>(shiftIds, {
          relations: SHIFT_RELATIONS,
          limit: 0,
          offset: 0,
          order: {},
        }),
      );
      return shiftsRecord.data;
    } else {
      return [];
    }
  }

  public async *fetchAbsences(
    dateRange: DateRange,
    absenceIds: string[],
  ): AsyncGenerator<AbsenceWithDecisionAndHighlighted[]> {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    // We chunk the absence ids to avoid the 2000 request limit
    const absenceIdsChunks = chunk(absenceIds, 2000);
    for (const absenceIdsChunk of absenceIdsChunks) {
      let absences: Absence[] = [];
      try {
        absences = await firstValueFrom(
          this.absencesService.getByIds(absenceIdsChunk),
        );
      } catch (error) {
        captureException(error);
        this.nzMessageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
        absences = [];
      }
      yield absences as AbsenceWithDecisionAndHighlighted[];
    }
  }

  public populateUserAssignments(
    user: UserTimelines,
    shifts: TimelineShift[],
    absences: (Absence & { id: string; isHighlighted: boolean })[],
    isCellView: boolean,
  ): void {
    const shiftsByUser = shifts.filter((shift) => shift.userId === user.id);
    const absencesByUser = absences.filter(
      (absence) => absence.userId === user.id,
    );

    shiftsByUser.forEach((shift) => {
      user.shiftsWithoutActivitiesDictionary[shift.id] = shift;
    });
    absencesByUser.forEach((absence) => {
      user.absencesDictionary[absence.id] = absence;
    });
    if (isCellView) {
      user.assignmentStacks = calculateUserAssignmentStack(
        user,
        isDebugEnabled(),
      );
    } else {
      user.determinedRowDataV2 = determineAssignmentsOverlaps({
        shifts: Object.values(user.shiftsWithoutActivitiesDictionary),
        absences: Object.values(user.absencesDictionary),
        stays: Object.values(user.staysDictionary),
        isDebugEnabled: isDebugEnabled(),
      });
    }
  }

  public async getUnassignedShifts(
    unassignedShiftIds: string[],
  ): Promise<UnassignedShift[]> {
    if (unassignedShiftIds.length) {
      const unassignedShiftsRecord = await firstValueFrom(
        this.shiftsService.getShiftsByIds<UnassignedShift>(unassignedShiftIds, {
          relations: SHIFT_RELATIONS,
          limit: 0,
          offset: 0,
          order: {},
        }),
      );
      return unassignedShiftsRecord.data.map((shift) => {
        shift.userId = null;
        return shift;
      });
    } else {
      return [];
    }
  }
}
